import React, { useRef, useState } from "react";
import "./language-picker.css";
import { Icon } from "@/components/ui-components/icon";
import { PopupMenu, type PopupMenuItem } from "@/components/ui-components/popup-menu";
import { LOCALES, SUPPORTED_LOCALES, type LocaleCode } from "@/i18n/locales";

interface Props {
    locale: LocaleCode;
    label: string;
    onChange: (locale: LocaleCode) => void;
}

type ViewTransitionDocument = Document & {
    startViewTransition?: (updateCallback: () => void) => { finished: Promise<unknown> };
};

export function LanguagePicker({ locale, label, onChange }: Props) {
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const current = LOCALES[locale];
    const available = SUPPORTED_LOCALES.filter(code => code !== locale);

    const changeLocale = (code: LocaleCode) => {
        setOpen(false);
        const viewTransitionDocument = document as ViewTransitionDocument;
        if (typeof viewTransitionDocument.startViewTransition !== "function") {
            onChange(code);
            return;
        }

        document.documentElement.dataset.localeTransition = "view";
        try {
            const transition = viewTransitionDocument.startViewTransition(() => onChange(code));
            const clearTransitionMarker = () => {
                window.setTimeout(() => {
                    delete document.documentElement.dataset.localeTransition;
                }, 800);
            };
            void transition.finished.then(clearTransitionMarker, clearTransitionMarker);
        } catch {
            delete document.documentElement.dataset.localeTransition;
            onChange(code);
        }
    };

    const items: PopupMenuItem[] = available.map(code => {
        const option = LOCALES[code];
        return {
            id: `language-${code}`,
            label: (
                <span className="language-option__content" lang={code} dir={option.direction}>
                    <span className="language-option__copy">
                        <strong>{option.nativeName}</strong>
                        <small>{option.shortLabel}</small>
                    </span>
                    <span className="language-option__code">{code.toUpperCase()}</span>
                </span>
            ),
            onSelect: () => {
                changeLocale(code);
            },
        };
    });

    return (
        <div className="language-picker">
            <button ref={triggerRef} className="header-btn" type="button" aria-label={`${label}, ${current.nativeName}`} aria-haspopup="menu" aria-expanded={open} aria-controls="language-menu" onClick={() => setOpen(value => !value)}>
                <Icon name="globe" />
                <span className="header-copy"><small>{label}</small><strong>{current.shortLabel}</strong></span>
            </button>
            <PopupMenu
                open={open}
                onClose={() => setOpen(false)}
                anchorRef={triggerRef}
                id="language-menu"
                placement="bottom"
                align="start"
                offset={0}
                className="user-menu language-menu"
                itemClassName="user-menu__item language-option"
                width={176}
                minWidth={176}
                maxWidth={176}
                items={items}
            />
        </div>
    );
}
